import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Navbar from "../Navbar/Main";
import Footer from "../Footer/Main";
import ShareToolsBar from "./ShareToolsBar";
import PageFeedbackCard from "./PageFeedbackCard";
import "./imf.css";

const getYouTubeVideoId = (value) => {
  if (!value || typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const directId = trimmed.match(/^[a-zA-Z0-9_-]{11}$/);
  if (directId) return directId[0];

  try {
    const url = new URL(trimmed);
    const host = url.hostname.replace(/^www\./, "");

    if (host === "youtu.be") {
      const shortId = url.pathname.split("/").filter(Boolean)[0];
      return shortId && shortId.length === 11 ? shortId : null;
    }

    if (host.includes("youtube.com")) {
      const fromQuery = url.searchParams.get("v");
      if (fromQuery && fromQuery.length === 11) return fromQuery;

      const pathParts = url.pathname.split("/").filter(Boolean);
      const embedIndex = pathParts.findIndex((part) => part === "embed" || part === "shorts");
      if (embedIndex >= 0 && pathParts[embedIndex + 1]) {
        const candidate = pathParts[embedIndex + 1];
        return candidate.length === 11 ? candidate : null;
      }
    }
  } catch (error) {
    return null;
  }

  return null;
};

const getItemUrlCandidate = (item) => {
  if (!item || typeof item !== "object") return "";
  return (
    item.youtube_url ||
    item.video_url ||
    item.url ||
    item.link ||
    item.href ||
    item.path ||
    ""
  );
};

const IMF = () => {
  const [links, setLinks] = useState([]);
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get("/links")
      .then((response) => {
        setLinks(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        console.error("Failed to load /links:", error);
        setLinks([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    axios
      .get("/publications")
      .then((response) => {
        setPublications(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        console.error("Failed to load /publications:", error);
        setPublications([]);
      });
  }, []);

  const videos = useMemo(() => {
    return links
      .map((item, index) => {
        const url = getItemUrlCandidate(item);
        const videoId = getYouTubeVideoId(url);
        if (!videoId) return null;

        return {
          id: item.id ?? index,
          title: item.title || item.keyword || `Video ${index + 1}`,
          embedUrl: `https://www.youtube.com/embed/${videoId}?rel=0&playsinline=1`,
        };
      })
      .filter(Boolean);
  }, [links]);

  return (
    <>
      <Navbar />
      <div className="stricky-header stricked-menu main-menu">
        <div className="sticky-header__content"></div>
      </div>

      <section className="page-header">
        <div className="page-header__bg"></div>
        <div className="container">
          <ul className="thm-breadcrumb list-unstyled">
            <li>
              <Link to="/">Home</Link>
            </li>
            <li>
              <Link to="/Publications">Publications</Link>
            </li>
            <li>
              <span>IMF Videos</span>
            </li>
          </ul>
        </div>
      </section>

      <section className="service-five pt-50 pb-50">
        <div className="container">
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
            <div className="block-title text-left mb-0">
              <p className="block-title__tagline">IMF video library</p>
            </div>
            <div className="flex-shrink-0">
              <ShareToolsBar pageTitle="IMF Videos — National Bank of Rwanda" />
            </div>
          </div>

          {loading ? (
            <p className="text-muted">Loading videos...</p>
          ) : videos.length === 0 ? (
            <p className="text-muted">No YouTube videos found in /links records.</p>
          ) : (
            <div className="row row-gutter-y-20">
              {videos.map((video) => (
                <div className="col-12 col-md-6 col-lg-4" key={video.id}>
                  <div className="imf-video-tile">
                    <iframe
                      src={video.embedUrl}
                      title={video.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      referrerPolicy="strict-origin-when-cross-origin"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="service-five pt-60 pb-50 publications-bottom-section">
        <div className="container">
          <div className="row row-gutter-y-30">
            <div className="col-lg-8">
              <div className="service-card">
                <div className="service-card__content">
                  <div className="service-card__content__inner">
                    <h3 className="service-card__title mb-3">Related links </h3>
                    <div className="row">
                      {publications.map((p) => (
                        <div className="col-md-6 mb-2" key={`all-${p.id}`}>
                          <Link to={p.link}>
                            <i className="fa fa-angle-right"></i> {p.title}
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <PageFeedbackCard pagePath="/imf" />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default IMF;
